import { Search } from 'lucide-react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import type { DunningReviewRow } from '@staff-app/shared-types';

/**
 * Filters above the combined Dunning Review table (requester, 2026-09-23): a search box plus
 * one dropdown per status column — the Devices Fulfillment filter bar's shape. Client-side:
 * both lists already arrive whole.
 *
 * Each dropdown's values are the SAME buckets its column's badge shows, derived here from the
 * same fields, so "Deactivated" in the filter means exactly the rows showing "Deactivated".
 */

export const ALL = '__all__';

export type StageKey = 'review' | 'suspended';
export type AccountKey = 'activated' | 'trial' | 'trial_end' | 'deactivated';
export type SubscriptionKey =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'unpaid'
  | 'canceled'
  | 'incomplete'
  | 'expired'
  | 'other'
  | 'none';
export type DunningKey = 'stopped' | 'paused' | 'none';

export interface DunningFilterState {
  search: string;
  stage: StageKey | typeof ALL;
  account: AccountKey | typeof ALL;
  subscription: SubscriptionKey | typeof ALL;
  dunning: DunningKey | typeof ALL;
}

/** Every filter unset — "show everything". */
export const EMPTY_FILTERS: DunningFilterState = {
  search: '',
  stage: ALL,
  account: ALL,
  subscription: ALL,
  dunning: ALL,
};

/**
 * What the page OPENS with (requester, 2026-09-23): Stage = Awaiting decision, so the table
 * leads with the accounts that need someone to act. Clear returns here, not to EMPTY_FILTERS.
 */
export const DEFAULT_FILTERS: DunningFilterState = { ...EMPTY_FILTERS, stage: 'review' };

/** Same (isTrial, isActive) buckets as `accountStatus()` — missing counts as false. */
export function accountKey(row: Pick<DunningReviewRow, 'isTrial' | 'isActive'>): AccountKey {
  const trial = !!row.isTrial;
  const active = !!row.isActive;
  if (trial && active) return 'trial';
  if (trial && !active) return 'trial_end';
  if (!trial && active) return 'activated';
  return 'deactivated';
}

const KNOWN_SUBS: SubscriptionKey[] = [
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
];

export function subscriptionKey(
  row: Pick<DunningReviewRow, 'subscriptionStatus'>,
): SubscriptionKey {
  const s = row.subscriptionStatus;
  if (!s) return 'none';
  if (s === 'incomplete_expired') return 'expired';
  return (KNOWN_SUBS as string[]).includes(s) ? (s as SubscriptionKey) : 'other';
}

export function dunningKey(row: Pick<DunningReviewRow, 'dunningExclusion'>): DunningKey {
  if (row.dunningExclusion === 'true') return 'stopped';
  if (row.dunningExclusion === 'pause') return 'paused';
  return 'none';
}

/** True when the filters differ from what the page opens with — i.e. there is something to Clear. */
export function hasActiveFilters(f: DunningFilterState): boolean {
  return (
    !!f.search.trim() ||
    f.stage !== DEFAULT_FILTERS.stage ||
    f.account !== DEFAULT_FILTERS.account ||
    f.subscription !== DEFAULT_FILTERS.subscription ||
    f.dunning !== DEFAULT_FILTERS.dunning
  );
}

/**
 * Keep the items whose row (and stage) match every set filter. Search — the same promise as the
 * Dunning Exclusion List's box, "email, domain or Stripe ID": case-insensitive substring over
 * company name, Stripe id, owner email and billing email (so `acme.com` matches a domain).
 */
export function applyDunningFilters<T extends { row: DunningReviewRow; stage: StageKey }>(
  items: T[],
  f: DunningFilterState,
): T[] {
  const q = f.search.trim().toLowerCase();
  return items.filter(({ row, stage }) => {
    if (q) {
      const hay = [row.companyName, row.stripeId, row.ownerEmail, row.billingEmail]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.stage !== ALL && stage !== f.stage) return false;
    if (f.account !== ALL && accountKey(row) !== f.account) return false;
    if (f.subscription !== ALL && subscriptionKey(row) !== f.subscription) return false;
    if (f.dunning !== ALL && dunningKey(row) !== f.dunning) return false;
    return true;
  });
}

const STAGE_OPTIONS: [StageKey, string][] = [
  ['review', 'Awaiting decision'],
  ['suspended', 'Deactivated'],
];
const ACCOUNT_OPTIONS: [AccountKey, string][] = [
  ['activated', 'Activated'],
  ['trial', 'Trial'],
  ['trial_end', 'Trial end'],
  ['deactivated', 'Deactivated'],
];
const SUBSCRIPTION_OPTIONS: [SubscriptionKey, string][] = [
  ['active', 'Active'],
  ['trialing', 'Trialing'],
  ['past_due', 'Past due'],
  ['unpaid', 'Unpaid'],
  ['canceled', 'Canceled'],
  ['incomplete', 'Incomplete'],
  ['expired', 'Expired'],
  ['other', 'Other'],
  ['none', 'No subscription'],
];
const DUNNING_OPTIONS: [DunningKey, string][] = [
  ['stopped', 'Stopped'],
  ['paused', 'Paused'],
  ['none', 'Not excluded'],
];

function FilterSelect<K extends string>({
  value,
  onChange,
  allLabel,
  options,
  testId,
  width = 'w-[170px]',
}: {
  value: K | typeof ALL;
  onChange: (v: K | typeof ALL) => void;
  allLabel: string;
  options: [K, string][];
  testId: string;
  width?: string;
}) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as K | typeof ALL)}>
      <SelectTrigger
        className={`${width} cursor-pointer`}
        aria-label={allLabel}
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL} className="cursor-pointer">
          {allLabel}
        </SelectItem>
        {options.map(([k, label]) => (
          <SelectItem key={k} value={k} className="cursor-pointer">
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function DunningFilterBar({
  value,
  onChange,
}: {
  value: DunningFilterState;
  onChange: (next: DunningFilterState) => void;
}) {
  const set = <K extends keyof DunningFilterState>(k: K, v: DunningFilterState[K]) =>
    onChange({ ...value, [k]: v });
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="dunning-filters">
      <div className="relative min-w-[240px] max-w-sm flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={value.search}
          onChange={(e) => set('search', e.target.value)}
          placeholder="Search email, domain or Stripe ID"
          className="pl-9"
          aria-label="Search accounts"
          data-testid="dunning-filter-search"
        />
      </div>
      <FilterSelect
        value={value.stage}
        onChange={(v) => set('stage', v)}
        allLabel="All stages"
        options={STAGE_OPTIONS}
        testId="dunning-filter-stage"
      />
      <FilterSelect
        value={value.account}
        onChange={(v) => set('account', v)}
        allLabel="All account statuses"
        options={ACCOUNT_OPTIONS}
        testId="dunning-filter-account"
        width="w-[190px]"
      />
      <FilterSelect
        value={value.subscription}
        onChange={(v) => set('subscription', v)}
        allLabel="All subscription statuses"
        options={SUBSCRIPTION_OPTIONS}
        testId="dunning-filter-subscription"
        width="w-[210px]"
      />
      <FilterSelect
        value={value.dunning}
        onChange={(v) => set('dunning', v)}
        allLabel="All dunning statuses"
        options={DUNNING_OPTIONS}
        testId="dunning-filter-dunning"
        width="w-[190px]"
      />
      {hasActiveFilters(value) ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange(DEFAULT_FILTERS)}
          data-testid="dunning-filter-clear"
        >
          {'Clear'}
        </Button>
      ) : null}
    </div>
  );
}
